import type {
  ProjectCockpitScopeDisplayStatus,
  ProjectCockpitScopeRow,
  ProjectCockpitScopeStatus,
  ProjectCockpitView,
  ScopeCommercialFacet,
  ScopeWorkTypeFacet,
  SourceRef,
  SystemFacet
} from "@automatry/schemas";

export type ScopeMatrixStatusFilter = "all" | ProjectCockpitScopeStatus;

export type ScopeResponsibilityFacet = ProjectCockpitScopeStatus;

export interface ScopeMatrixFacetValues {
  responsibility: ScopeResponsibilityFacet;
  system?: SystemFacet;
  workType?: ScopeWorkTypeFacet;
  commercial?: ScopeCommercialFacet;
}

export interface ScopeMatrixDisplayRow {
  key: string;
  id: string;
  label: string;
  status: ProjectCockpitScopeStatus;
  displayStatus: ProjectCockpitScopeDisplayStatus;
  statusLabel: string;
  facets: ScopeMatrixFacetValues;
  rationale: string;
  sourceLinkStatus: "available" | "not_produced";
  sourceLinkLabel: string;
  sourceLinkDisplayLabel: string;
  sourceRefs: SourceRef[];
  sourceRefCount: number;
  ruleTraceRefs: string[];
  ruleTraceRefCount: number;
  generatedRowNumber: number;
  generatedRowCount: number;
  isRepeatedGeneratedLine: boolean;
  isNotable: boolean;
  notableLabel?: string;
  searchText: string;
}

export interface ScopeMatrixDisplayGroup {
  id: ProjectCockpitScopeStatus;
  label: string;
  description: string;
  rows: ScopeMatrixDisplayRow[];
}

export interface ScopeMatrixDisplayViewModel {
  projectId: string;
  status: ProjectCockpitView["status"];
  updatedAt: string;
  generatedFrom?: string;
  rows: ScopeMatrixDisplayRow[];
  groups: ScopeMatrixDisplayGroup[];
  notableRows: ScopeMatrixDisplayRow[];
  counts: {
    included: number;
    byOthers: number;
    excluded: number;
  };
  generatedCounts: {
    included: number;
    byOthers: number;
    excluded: number;
  };
  realRowCount: number;
  curatedRowCount: number;
  fabricatedRowCount: number;
  distinctGeneratedLineCount: number;
  sourceLinkNotProducedCount: number;
  rationaleProducedCount: number;
  dataGaps: string[];
  installNoteFooter: string;
}

const statusOrder: ProjectCockpitScopeStatus[] = ["included", "by_others", "excluded"];

const statusCopy: Record<
  ProjectCockpitScopeStatus,
  {
    displayStatus: ProjectCockpitScopeDisplayStatus;
    label: string;
    groupDescription: string;
  }
> = {
  included: {
    displayStatus: "included",
    label: "Included",
    groupDescription: "Controls supply and controls-facing works carried by Titan."
  },
  by_others: {
    displayStatus: "byOthers",
    label: "By others",
    groupDescription:
      "Installer, electrical, client or third-party works that the controls output calls out but does not price as Titan scope."
  },
  excluded: {
    displayStatus: "excluded",
    label: "Excluded",
    groupDescription:
      "Assumptions or install-scope lines explicitly outside the generated controls package."
  }
};

const notableMatchers: {
  id: string;
  label: string;
  test: (row: ProjectCockpitScopeRow) => boolean;
}[] = [
  {
    id: "switch-supply",
    label: "Switch supply",
    test: (row) => /network switch supply/i.test(row.title)
  },
  {
    id: "spur-provision",
    label: "Spur provision",
    test: (row) => /spur provision/i.test(row.title)
  },
  {
    id: "containment",
    label: "Containment",
    test: (row) => /containment/i.test(row.title)
  },
  {
    id: "client-switches",
    label: "Client switch assumption",
    test: (row) => /client-supplied network switches/i.test(row.title)
  },
  {
    id: "termination",
    label: "Final termination",
    test: (row) => /termination/i.test(row.title)
  }
];

const signatureFor = (row: ProjectCockpitScopeRow): string =>
  [row.title, row.status, row.rationale ?? ""].join("\u001f");

const countBySignature = (
  rows: readonly ProjectCockpitScopeRow[]
): Map<string, number> => {
  const counts = new Map<string, number>();

  rows.forEach((row) => {
    const signature = signatureFor(row);
    counts.set(signature, (counts.get(signature) ?? 0) + 1);
  });

  return counts;
};

const notableLabelFor = (row: ProjectCockpitScopeRow): string | undefined =>
  notableMatchers.find((matcher) => matcher.test(row))?.label;

const sourceLinkDisplayLabel = (
  status: ScopeMatrixDisplayRow["sourceLinkStatus"]
): string => (status === "available" ? "Source linked" : "Source link pending");

const scopeFacetsFor = (row: ProjectCockpitScopeRow): ScopeMatrixFacetValues => {
  const system = row.tags?.system;
  const workType = row.tags?.workType;
  const commercial = row.tags?.commercial;

  return {
    responsibility: row.status,
    ...(system ? { system } : {}),
    ...(workType ? { workType } : {}),
    ...(commercial ? { commercial } : {})
  };
};

const displayRows = (
  rows: readonly ProjectCockpitScopeRow[]
): ScopeMatrixDisplayRow[] => {
  const totals = countBySignature(rows);
  const seen = new Map<string, number>();

  return rows.map((row, index) => {
    const signature = signatureFor(row);
    const generatedRowNumber = (seen.get(signature) ?? 0) + 1;
    seen.set(signature, generatedRowNumber);

    const status = statusCopy[row.status] ?? statusCopy.included;
    const label = row.label ?? row.title;
    const sourceLinkStatus = row.sourceLinkStatus ?? "not_produced";
    const sourceLinkLabel = row.sourceLinkLabel ?? "Source links not yet produced";
    const sourceRefs = row.sourceRefs ?? [];
    const ruleTraceRefs = row.ruleTraceRefs ?? [];
    const sourceRefCount = row.sourceRefCount ?? sourceRefs.length;
    const ruleTraceRefCount = row.ruleTraceRefCount ?? ruleTraceRefs.length;
    const notableLabel = notableLabelFor(row);
    const rationale = row.rationale ?? "Reason not yet produced by the engine.";
    const facets = scopeFacetsFor(row);

    return {
      key: `${row.id}-${index}`,
      id: row.id,
      label,
      status: row.status,
      displayStatus: row.displayStatus ?? status.displayStatus,
      statusLabel: status.label,
      facets,
      rationale,
      sourceLinkStatus,
      sourceLinkLabel,
      sourceLinkDisplayLabel: sourceLinkDisplayLabel(sourceLinkStatus),
      sourceRefs,
      sourceRefCount,
      ruleTraceRefs,
      ruleTraceRefCount,
      generatedRowNumber,
      generatedRowCount: totals.get(signature) ?? 1,
      isRepeatedGeneratedLine: (totals.get(signature) ?? 1) > 1,
      isNotable: notableLabel !== undefined,
      ...(notableLabel ? { notableLabel } : {}),
      searchText: [
        row.id,
        label,
        row.status,
        status.label,
        rationale,
        sourceLinkDisplayLabel(sourceLinkStatus),
        sourceLinkLabel,
        notableLabel ?? ""
      ]
        .join(" ")
        .toLowerCase()
    };
  });
};

const scopeInstallNoteFooter =
  "Install items appear only as notes, never priced quantities - e.g. Belden 9841 MS/TP bus required for the controller trunk.";

interface CuratedScopeBucket {
  id: string;
  label: string;
  status: ProjectCockpitScopeStatus;
  rationale: string;
  notableLabel?: string;
  match: (row: ScopeMatrixDisplayRow) => boolean;
}

const curatedScopeBuckets: CuratedScopeBucket[] = [
  {
    id: "scope-curated-network-switches",
    label: "Supply of network switches",
    status: "included",
    rationale:
      "We supply and price these - Titan include the controls network switches rather than excluding them from the kit.",
    notableLabel: "Switch supply",
    match: (row) =>
      /network switch supply/i.test(row.label) && !/client-supplied/i.test(row.label)
  },
  {
    id: "scope-curated-control-panel-fabrication",
    label: "Control panel fabrication",
    status: "included",
    rationale:
      "Pre-wired controllers, relays, panels and final controls terminations are built, tested and commissioned by Titan.",
    notableLabel: "Controls supply",
    match: (row) =>
      /titan controls equipment supply|central load-relay outputs|final controls termination/i.test(
        row.label
      )
  },
  {
    id: "scope-curated-electrical-spurs",
    label: "Electrical spur termination",
    status: "by_others",
    rationale:
      "Final connection to fused spurs, upstream protection and isolation remains with the electrical contractor.",
    notableLabel: "Spur provision",
    match: (row) => /spur provision/i.test(row.label)
  },
  {
    id: "scope-curated-containment",
    label: "Containment & cable tray",
    status: "excluded",
    rationale:
      "Cable routes, containment, tray, sundries and client-supplied switch assumptions sit outside the priced controls package.",
    notableLabel: "Install scope",
    match: (row) =>
      /field cabling and containment|client-supplied network switches assumption/i.test(
        row.label
      )
  },
  {
    id: "scope-curated-bms-head-end",
    label: "BMS head-end integration",
    status: "included",
    rationale:
      "Third-party protocol gateways and BMS-facing controls integration are carried in the controls package for commissioning.",
    notableLabel: "BMS integration",
    match: (row) => /third-party protocol gateways/i.test(row.label)
  }
];

const uniqueSourceRefs = (rows: readonly ScopeMatrixDisplayRow[]): SourceRef[] => {
  const refs = new Map<string, SourceRef>();

  rows.forEach((row) => {
    row.sourceRefs.forEach((ref) => {
      refs.set(JSON.stringify(ref), ref);
    });
  });

  return [...refs.values()];
};

const uniqueRuleTraceRefs = (rows: readonly ScopeMatrixDisplayRow[]): string[] => [
  ...new Set(rows.flatMap((row) => row.ruleTraceRefs))
];

const uniqueFacetValue = <T extends string>(
  values: readonly (T | undefined)[]
): T | undefined => {
  const uniqueValues = [...new Set(values.filter((value): value is T => !!value))];

  return uniqueValues.length === 1 ? uniqueValues[0] : undefined;
};

const aggregateScopeFacets = (
  bucket: CuratedScopeBucket,
  rows: readonly ScopeMatrixDisplayRow[]
): ScopeMatrixFacetValues => {
  const sourceSystem = rows.map((row) => row.facets.system);
  const sourceWorkType = rows.map((row) => row.facets.workType);
  const sourceCommercial = rows.map((row) => row.facets.commercial);
  const system = uniqueFacetValue(sourceSystem);
  const workType = uniqueFacetValue(sourceWorkType);
  const commercial = uniqueFacetValue(sourceCommercial);

  return {
    responsibility: bucket.status,
    ...(system ? { system } : {}),
    ...(workType ? { workType } : {}),
    ...(commercial ? { commercial } : {})
  };
};

const slugFor = (label: string): string =>
  label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "scope-row";

const aggregateScopeRows = (
  bucket: CuratedScopeBucket,
  rows: readonly ScopeMatrixDisplayRow[]
): ScopeMatrixDisplayRow => {
  const status = statusCopy[bucket.status] ?? statusCopy.included;
  const sourceRefs = uniqueSourceRefs(rows);
  const ruleTraceRefs = uniqueRuleTraceRefs(rows);
  const sourceLinkStatus = rows.some((row) => row.sourceLinkStatus === "available")
    ? "available"
    : "not_produced";
  const sourceLinkLabel =
    sourceRefs[0]?.locator ??
    rows.find((row) => row.sourceLinkStatus === "available")?.sourceLinkLabel ??
    "Source links not yet produced";
  const generatedRowCount = rows.length;
  const repeatedLabel =
    generatedRowCount === 1
      ? "1 generated line"
      : `${generatedRowCount} generated lines`;
  const facets = aggregateScopeFacets(bucket, rows);

  return {
    key: bucket.id,
    id: bucket.id,
    label: bucket.label,
    status: bucket.status,
    displayStatus: status.displayStatus,
    statusLabel: status.label,
    facets,
    rationale: bucket.rationale,
    sourceLinkStatus,
    sourceLinkLabel,
    sourceLinkDisplayLabel: sourceLinkDisplayLabel(sourceLinkStatus),
    sourceRefs,
    sourceRefCount: rows.reduce((sum, row) => sum + row.sourceRefCount, 0),
    ruleTraceRefs,
    ruleTraceRefCount: rows.reduce((sum, row) => sum + row.ruleTraceRefCount, 0),
    generatedRowNumber: 1,
    generatedRowCount,
    isRepeatedGeneratedLine: generatedRowCount > 1,
    isNotable: bucket.notableLabel !== undefined,
    ...(bucket.notableLabel ? { notableLabel: bucket.notableLabel } : {}),
    searchText: [
      bucket.id,
      bucket.label,
      bucket.status,
      status.label,
      bucket.rationale,
      bucket.notableLabel ?? "",
      repeatedLabel,
      ...rows.map((row) => row.searchText)
    ]
      .join(" ")
      .toLowerCase()
  };
};

const fallbackBucketsFor = (
  rows: readonly ScopeMatrixDisplayRow[]
): ScopeMatrixDisplayRow[] => {
  const grouped = new Map<string, ScopeMatrixDisplayRow[]>();

  rows.forEach((row) => {
    const signature = [row.label, row.status, row.rationale].join("\u001f");
    grouped.set(signature, [...(grouped.get(signature) ?? []), row]);
  });

  return [...grouped.values()].flatMap((bucketRows, index) => {
    const first = bucketRows[0];

    if (!first) {
      return [];
    }

    return aggregateScopeRows(
      {
        id: `scope-curated-${slugFor(first.label)}-${index + 1}`,
        label: first.label,
        status: first.status,
        rationale: first.rationale,
        ...(first.notableLabel ? { notableLabel: first.notableLabel } : {}),
        match: () => false
      },
      bucketRows
    );
  });
};

const curateScopeMatrixRows = (
  rows: readonly ScopeMatrixDisplayRow[]
): ScopeMatrixDisplayRow[] => {
  const remaining = new Set(rows.map((row) => row.key));
  const curatedRows: ScopeMatrixDisplayRow[] = [];

  curatedScopeBuckets.forEach((bucket) => {
    const bucketRows = rows.filter(
      (row) => remaining.has(row.key) && bucket.match(row)
    );

    if (bucketRows.length === 0) {
      return;
    }

    bucketRows.forEach((row) => remaining.delete(row.key));
    curatedRows.push(aggregateScopeRows(bucket, bucketRows));
  });

  const ungroupedRows = rows.filter((row) => remaining.has(row.key));

  return [...curatedRows, ...fallbackBucketsFor(ungroupedRows)];
};

export const scopeMatrixCounts = (rows: readonly ScopeMatrixDisplayRow[]) => ({
  included: rows.filter((row) => row.status === "included").length,
  byOthers: rows.filter((row) => row.status === "by_others").length,
  excluded: rows.filter((row) => row.status === "excluded").length
});

export const groupScopeMatrixRows = (
  rows: readonly ScopeMatrixDisplayRow[],
  statusFilter: ScopeMatrixStatusFilter = "all",
  query = ""
): ScopeMatrixDisplayGroup[] => {
  const trimmedQuery = query.trim().toLowerCase();
  const filteredRows: ScopeMatrixDisplayRow[] = rows
    .filter((row) => statusFilter === "all" || row.status === statusFilter)
    .filter((row) => !trimmedQuery || row.searchText.includes(trimmedQuery))
    .slice()
    .sort((first: ScopeMatrixDisplayRow, second: ScopeMatrixDisplayRow) => {
      const byStatus =
        statusOrder.indexOf(first.status) - statusOrder.indexOf(second.status);

      if (byStatus !== 0) {
        return byStatus;
      }

      const byLabel = first.label.localeCompare(second.label, "en-GB");

      if (byLabel !== 0) {
        return byLabel;
      }

      return first.generatedRowNumber - second.generatedRowNumber;
    });

  return statusOrder
    .map(
      (status): ScopeMatrixDisplayGroup => {
        const copy = statusCopy[status] ?? statusCopy.included;

        return {
          id: status,
          label: copy.label,
          description: copy.groupDescription,
          rows: filteredRows.filter(
            (row: ScopeMatrixDisplayRow) => row.status === status
          )
        };
      }
    )
    .filter((group) => group.rows.length > 0);
};

const notableRows = (rows: readonly ScopeMatrixDisplayRow[]): ScopeMatrixDisplayRow[] =>
  rows.filter((row) => row.isNotable);

const dataGapsFor = (rows: readonly ScopeMatrixDisplayRow[]): string[] => {
  if (rows.length === 0) {
    return ["Scope rows are not yet produced by the engine."];
  }

  const gaps: string[] = [];

  if (rows.some((row) => row.sourceLinkStatus === "not_produced")) {
    gaps.push(
      "Scope source links are not yet produced; each row is shown with the generated rationale only."
    );
  }

  if (rows.some((row) => row.rationale.length === 0)) {
    gaps.push("Some scope reasons are not yet produced by the engine.");
  }

  return gaps;
};

export const projectCockpitToScopeMatrixView = (
  cockpit: ProjectCockpitView
): ScopeMatrixDisplayViewModel => {
  const generatedRows = displayRows(cockpit.scopeRows ?? []);
  const rows = curateScopeMatrixRows(generatedRows);
  const groups = groupScopeMatrixRows(rows);
  const signatures = new Set((cockpit.scopeRows ?? []).map((row) => signatureFor(row)));

  return {
    projectId: cockpit.projectId,
    status: cockpit.status,
    updatedAt: cockpit.updatedAt,
    ...(cockpit.generatedFrom ? { generatedFrom: cockpit.generatedFrom } : {}),
    rows,
    groups,
    notableRows: notableRows(rows),
    counts: scopeMatrixCounts(rows),
    generatedCounts: scopeMatrixCounts(generatedRows),
    realRowCount: generatedRows.length,
    curatedRowCount: rows.length,
    fabricatedRowCount: 0,
    distinctGeneratedLineCount: signatures.size,
    sourceLinkNotProducedCount: generatedRows.filter(
      (row) => row.sourceLinkStatus === "not_produced"
    ).length,
    rationaleProducedCount: generatedRows.filter(
      (row) => row.rationale !== "Reason not yet produced by the engine."
    ).length,
    dataGaps: dataGapsFor(generatedRows),
    installNoteFooter: scopeInstallNoteFooter
  };
};
